import React, { useState } from 'react';
import { Copy, Check, Zap, Download, MessageSquare, FileText, FileDown, Loader2, AlertCircle, Play, Printer, Star } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getModeByLabel } from '../../constants/modes';
import OBGYN_HISTORY from '../../constants/obgynHistory';

import { API, getUserPhone } from '../../lib/api';

// ---- YouTube Video Embed Component ----
const YouTubeEmbed = ({ searchQuery }) => {
  const [expanded, setExpanded] = useState(false);
  
  // Generate YouTube search URL
  const searchUrl = `https://www.youtube.com/results?search_query=${encodeURIComponent(searchQuery)}`;
  
  // Popular curated video IDs for common searches
  const curatedVideos = {
    'pregnancy yoga': 'K4TOrB7at0Y',
    'prenatal yoga': 'B8YFU_jLbLc',
    'pregnancy meditation': '8J4xDfNkPuU',
    'fertility meditation': 'XJxFWKBq9rE',
    'pcos yoga': 'l5Uy8WHy8kI',
    'menopause yoga': 'Fw7LzHKKXQE',
    'breastfeeding latch': 'eMn7A3nlJTE',
    'kegel exercises': 'F8SHkEwmXBQ',
    'birth breathing': 'j2GAaGboUvs',
    'pregnancy mantras': 'dNJdJIwCF_Y',
    'garbha raksha': 'RwBTq0dVJco',
    'mozart pregnancy': 'df-eLzao63I',
    'postnatal yoga': 'GQiwkx4UJK4',
    'pregnancy ragas': 'jnxqP9OjTMk',
  };
  
  // Find a matching curated video or use search
  const lowerQuery = searchQuery.toLowerCase();
  let videoId = null;
  for (const [key, id] of Object.entries(curatedVideos)) {
    if (lowerQuery.includes(key) || key.includes(lowerQuery.split(' ').slice(0, 2).join(' '))) {
      videoId = id;
      break;
    }
  }
  
  if (expanded && videoId) {
    return (
      <div className="my-3 rounded-xl overflow-hidden border border-teal-200 bg-teal-50">
        <div className="aspect-video">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}?rel=0`}
            title={searchQuery}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
        <div className="px-3 py-2 flex items-center justify-between bg-white border-t border-teal-100">
          <span className="text-xs text-gray-600 truncate">{searchQuery}</span>
          <button
            onClick={() => setExpanded(false)}
            className="text-xs text-teal-600 hover:text-teal-800 font-medium"
          >
            Collapse
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="inline-flex items-center gap-1.5 my-1">
      <button
        onClick={() => videoId ? setExpanded(true) : window.open(searchUrl, '_blank')}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg text-xs font-medium text-red-700 transition-colors"
      >
        <Play className="w-3.5 h-3.5" fill="currentColor" />
        {videoId ? 'Watch Video' : 'Search YouTube'}
      </button>
      <a
        href={searchUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs text-gray-400 hover:text-red-600 hover:underline"
      >
        {searchQuery}
      </a>
    </div>
  );
};

// ---- Inline Markdown Parser ----
function parseInline(text) {
  if (!text) return null;
  
  // First, extract YouTube references [YouTube: search query]
  const youtubeRegex = /\[YouTube:\s*([^\]]+)\]/g;
  const segments = [];
  let lastIndex = 0;
  let match;
  
  while ((match = youtubeRegex.exec(text)) !== null) {
    // Add text before the YouTube link
    if (match.index > lastIndex) {
      segments.push({ type: 'text', content: text.slice(lastIndex, match.index) });
    }
    // Add YouTube component
    segments.push({ type: 'youtube', query: match[1].trim() });
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    segments.push({ type: 'text', content: text.slice(lastIndex) });
  }
  
  // If no YouTube links found, process as regular text
  if (segments.length === 0) {
    segments.push({ type: 'text', content: text });
  }
  
  return segments.map((segment, segIdx) => {
    if (segment.type === 'youtube') {
      return <YouTubeEmbed key={`yt-${segIdx}`} searchQuery={segment.query} />;
    }
    
    // Process regular text with markdown
    const parts = segment.content.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return (
          <strong key={`${segIdx}-${i}`} className="font-bold text-gray-900 bg-amber-50 px-0.5 rounded-sm">
            {part.slice(2, -2)}
          </strong>
        );
      }
      if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
        return <em key={`${segIdx}-${i}`} className="italic text-gray-700">{part.slice(1, -1)}</em>;
      }
      if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
        return (
          <code key={`${segIdx}-${i}`} className="bg-slate-100 text-rose-700 px-1.5 py-0.5 rounded text-xs font-mono border border-slate-200">
            {part.slice(1, -1)}
          </code>
        );
      }
      return part;
    });
  });
}

// ---- Block Markdown Parser ----
function parseBlocks(content) {
  if (!content) return [];
  const lines = content.split('\n');
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Empty line
    if (!line.trim()) { blocks.push({ type: 'empty' }); i++; continue; }

    // Horizontal rule
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line.trim())) {
      blocks.push({ type: 'hr' }); i++; continue;
    }

    // Table
    if (line.includes('|') && line.trim().startsWith('|') && lines[i + 1] && lines[i + 1].includes('---')) {
      const tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        tableLines.push(lines[i]); i++;
      }
      blocks.push({ type: 'table', lines: tableLines });
      continue;
    }

    // Code block / Mermaid diagram
    if (line.trim().startsWith('```')) {
      const lang = line.trim().slice(3).trim().toLowerCase();
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        codeLines.push(lines[i]); i++;
      }
      if (i < lines.length) i++; // skip closing ```
      if (lang === 'mermaid') {
        blocks.push({ type: 'mermaid', code: codeLines.join('\n') });
      } else {
        blocks.push({ type: 'code', lang, code: codeLines.join('\n') });
      }
      continue;
    }

    // Image: ![alt](url)
    const imgMatch = line.match(/^!\[([^\]]*)\]\(([^)]+)\)/);
    if (imgMatch) {
      blocks.push({ type: 'image', alt: imgMatch[1], url: imgMatch[2] });
      i++; continue;
    }

    // H1
    if (line.startsWith('# ')) { blocks.push({ type: 'h1', text: line.slice(2) }); i++; continue; }
    // H2
    if (line.startsWith('## ')) { blocks.push({ type: 'h2', text: line.slice(3) }); i++; continue; }
    // H3
    if (line.startsWith('### ')) { blocks.push({ type: 'h3', text: line.slice(4) }); i++; continue; }
    // H4 — treat as h3
    if (line.startsWith('#### ')) { blocks.push({ type: 'h3', text: line.slice(5) }); i++; continue; }
    // H5/H6 — treat as section header
    if (line.startsWith('##### ')) { blocks.push({ type: 'section_header', text: line.slice(6) }); i++; continue; }
    if (line.startsWith('###### ')) { blocks.push({ type: 'section_header', text: line.slice(7) }); i++; continue; }

    // Bold-only line = section header
    if (/^\*\*[^*]+\*\*\s*$/.test(line.trim())) {
      blocks.push({ type: 'section_header', text: line.trim().replace(/^\*\*|\*\*$/g, '').trim() });
      i++; continue;
    }

    // Numbered list
    if (/^\d+[\.\)]\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+[\.\)]\s/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+[\.\)]\s/, '')); i++;
      }
      blocks.push({ type: 'ordered_list', items }); continue;
    }

    // Bullet list
    if (/^[-•*]\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-•*]\s/.test(lines[i])) {
        items.push(lines[i].replace(/^[-•*]\s/, '')); i++;
      }
      blocks.push({ type: 'unordered_list', items }); continue;
    }

    // Blockquote
    if (line.startsWith('> ')) {
      blocks.push({ type: 'blockquote', text: line.slice(2) }); i++; continue;
    }

    // Paragraph
    blocks.push({ type: 'paragraph', text: line }); i++;
  }
  return blocks;
}

function renderTable(lines) {
  const rows = lines.map(l => l.split('|').map(c => c.trim()).filter(c => c !== ''));
  const headers = rows[0] || [];
  const dataRows = rows.slice(2); // skip separator row
  return (
    <div className="overflow-x-auto my-3 rounded-xl border border-gray-200">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-raspberry-800">
            {headers.map((h, i) => (
              <th key={i} className="px-3 py-2.5 text-left font-bold text-white">{parseInline(h)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dataRows.map((row, ri) => (
            <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-raspberry-50/30'}>
              {row.map((cell, ci) => (
                <td key={ci} className="px-3 py-2 text-gray-700 border-b border-gray-100">{parseInline(cell)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Mermaid.js diagram renderer
function MermaidBlock({ code }) {
  const ref = React.useRef(null);
  const [svg, setSvg] = React.useState('');
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    import('https://cdn.jsdelivr.net/npm/mermaid@11/dist/mermaid.esm.min.mjs')
      .then(({ default: mermaid }) => {
        mermaid.initialize({ startOnLoad: false, theme: 'default', securityLevel: 'loose' });
        const id = `mermaid-${Math.random().toString(36).slice(2)}`;
        return mermaid.render(id, code);
      })
      .then(({ svg: rendered }) => {
        if (!cancelled) setSvg(rendered);
      })
      .catch(err => {
        if (!cancelled) setError(err.message);
      });
    return () => { cancelled = true; };
  }, [code]);

  if (error) {
    return (
      <pre className="bg-gray-100 text-gray-700 rounded-lg p-3 overflow-x-auto my-2 text-xs border border-gray-200">
        <code>{code}</code>
      </pre>
    );
  }

  if (!svg) return <div className="animate-pulse bg-gray-100 h-32 rounded-lg my-2" />;

  return (
    <div
      ref={ref}
      className="my-3 p-4 bg-white rounded-lg border border-gray-200 overflow-x-auto"
      data-testid="mermaid-diagram"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

function MarkdownRenderer({ content }) {
  const blocks = parseBlocks(content);

  return (
    <div className="space-y-1">
      {blocks.map((block, i) => {
        switch (block.type) {
          case 'empty':
            return <div key={i} className="h-1" />;
          case 'hr':
            return <hr key={i} className="border-gray-100 my-3" />;
          case 'h1':
            return (
              <h2 key={i} className="text-base font-black text-gray-900 mt-5 mb-2 font-heading tracking-tight flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-raspberry-600 flex-shrink-0 inline-block" />
                {parseInline(block.text)}
              </h2>
            );
          case 'h2':
            return (
              <h3 key={i} className="text-sm font-bold text-raspberry-800 mt-4 mb-1.5 font-heading pb-1 border-b border-raspberry-100">
                {parseInline(block.text)}
              </h3>
            );
          case 'h3':
            return (
              <h4 key={i} className="text-sm font-bold text-raspberry-700 mt-3 mb-1 font-heading">
                {parseInline(block.text)}
              </h4>
            );
          case 'section_header': {
            return (
              <div key={i} className="mt-4 mb-2 flex items-center gap-2">
                <span className="w-1 h-4 rounded-full bg-raspberry-500 flex-shrink-0" />
                <h3 className="text-xs font-black text-raspberry-900 uppercase tracking-[0.12em] font-heading">
                  {parseInline(block.text)}
                </h3>
              </div>
            );
          }
          case 'ordered_list':
            return (
              <ol key={i} className="space-y-2 my-2">
                {block.items.map((item, j) => (
                  <li key={j} className="flex gap-2.5 text-sm text-gray-700">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-raspberry-600 text-white text-[10px] flex items-center justify-center font-bold mt-0.5 shadow-sm">
                      {j + 1}
                    </span>
                    <span className="leading-relaxed pt-0.5">{parseInline(item)}</span>
                  </li>
                ))}
              </ol>
            );
          case 'unordered_list':
            return (
              <ul key={i} className="space-y-1.5 my-2">
                {block.items.map((item, j) => (
                  <li key={j} className="flex gap-2.5 text-sm text-gray-700">
                    <span className="flex-shrink-0 w-2 h-2 rounded-full bg-raspberry-400 mt-2 shadow-sm" />
                    <span className="leading-relaxed">{parseInline(item)}</span>
                  </li>
                ))}
              </ul>
            );
          case 'blockquote': {
            const txt = block.text.trim();
            const isRedFlag = /^(RED FLAG|WARNING|CRITICAL|CAUTION|ALERT):/i.test(txt);
            const isNote = /^NOTE:/i.test(txt);
            return (
              <blockquote
                key={i}
                className={`pl-3 border-l-[3px] rounded-r-xl py-2 pr-3 my-2 text-sm leading-relaxed ${
                  isRedFlag
                    ? 'border-red-500 bg-red-50 text-red-800 font-medium'
                    : isNote
                    ? 'border-blue-400 bg-blue-50 text-blue-800'
                    : 'border-raspberry-300 bg-raspberry-50/40 italic text-gray-600'
                }`}
              >
                {parseInline(txt)}
              </blockquote>
            );
          }
          case 'table':
            return <div key={i}>{renderTable(block.lines)}</div>;
          case 'mermaid':
            return <MermaidBlock key={i} code={block.code} />;
          case 'code':
            return (
              <pre key={i} className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto my-2 text-xs">
                <code>{block.code}</code>
              </pre>
            );
          case 'image':
            return (
              <div key={i} className="my-3">
                <img src={block.url} alt={block.alt} className="max-w-full rounded-lg border border-gray-200 shadow-sm" data-testid="generated-image" />
                {block.alt && <p className="text-xs text-gray-500 mt-1 italic">{block.alt}</p>}
              </div>
            );
          case 'paragraph':
            return (
              <p key={i} className="text-sm text-gray-700 leading-relaxed">
                {parseInline(block.text)}
              </p>
            );
          default:
            return null;
        }
      })}
    </div>
  );
}

export const MessageBubble = ({ message, onLatestEvidence }) => {
  const [copied, setCopied] = useState(false);
  const [starred, setStarred] = useState(false);
  const [showWhatsAppForm, setShowWhatsAppForm] = useState(false);
  const [waDocName, setWaDocName] = useState(() => localStorage.getItem('gynovate_doc_name') || '');
  const [waClinicName, setWaClinicName] = useState(() => localStorage.getItem('gynovate_clinic_name') || '');
  const [exportingPptx, setExportingPptx] = useState(false);
  const isUser = message.role === 'user';
  const mode = getModeByLabel(message.mode);

  // Robust clipboard write — falls back to execCommand on browsers/WebViews where
  // navigator.clipboard is undefined, blocked, or throws silently (common on Android WebView).
  const copyToClipboard = async (text) => {
    // Try modern API first
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch {
        /* fall through to legacy path */
      }
    }
    // Legacy fallback
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.top = '0';
      ta.style.left = '0';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      ta.setSelectionRange(0, ta.value.length);
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  };

  const handleCopy = async () => {
    const ok = await copyToClipboard(message.content);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Copied to clipboard');
    } else {
      toast.error('Copy failed — long-press the text to copy manually');
    }
  };

  const handleWhatsAppCopy = async () => {
    const wText = message.content
      .replace(/\*\*([^*]+)\*\*/g, '*$1*')
      .replace(/^## ([^\n]+)/gm, '*$1*')
      .replace(/^# ([^\n]+)/gm, '*$1*')
      .replace(/^- /gm, '• ')
      .replace(/^\d+\. /gm, (m) => m);
    const ok = await copyToClipboard(wText);
    if (ok) toast.success('Copied for WhatsApp! Paste in your chat.');
    else toast.error('Copy failed — long-press the text to copy manually');
  };

  const handleExportPDF = () => {
    const win = window.open('', '_blank');
    if (!win) { toast.error('Pop-up blocked. Allow pop-ups and try again.'); return; }
    
    // Convert markdown to HTML for better print formatting
    const content = message.content
      .replace(/^### (.*$)/gm, '<h3 style="color:#9D174D;margin-top:18px;margin-bottom:8px;font-size:15px">$1</h3>')
      .replace(/^## (.*$)/gm, '<h2 style="color:#9D174D;margin-top:22px;margin-bottom:10px;font-size:17px">$1</h2>')
      .replace(/^# (.*$)/gm, '<h1 style="color:#0f172a;margin-top:24px;margin-bottom:12px;font-size:20px">$1</h1>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/^- (.*$)/gm, '<li style="margin:2px 0;padding-left:4px">$1</li>')
      .replace(/(<li.*<\/li>\n?)+/g, '<ul style="margin:6px 0 6px 16px;padding:0">$&</ul>')
      .replace(/^\|(.*)\|$/gm, (match) => {
        const cells = match.split('|').filter(c => c.trim());
        return '<tr>' + cells.map(c => `<td style="border:1px solid #e2e8f0;padding:6px 10px;font-size:13px">${c.trim()}</td>`).join('') + '</tr>';
      })
      .replace(/(<tr>.*<\/tr>\n?)+/g, '<table style="border-collapse:collapse;width:100%;margin:10px 0">$&</table>')
      .replace(/^(?!<[hultro])(.*\S.*)$/gm, '<p style="margin:4px 0;line-height:1.6">$1</p>')
      .replace(/_{3,}/g, '<hr style="border:none;border-top:1px solid #e2e8f0;margin:12px 0">')
      .replace(/___+|___(.*?)___/g, '<span style="text-decoration:underline">$1</span>');

    win.document.write(`<!DOCTYPE html><html><head><title>Gynovate AI/SI - ${message.mode || 'Clinical Output'}</title>
<style>
@media print { body { padding: 20px !important; } .no-print { display:none !important; } @page { margin: 1cm; } }
body { font-family: 'Segoe UI', Arial, sans-serif; padding: 32px 40px; max-width: 820px; margin: 0 auto; color: #1e293b; font-size: 14px; line-height: 1.6; }
.header { border-bottom: 2px solid #9D174D; padding-bottom: 12px; margin-bottom: 20px; }
.header h2 { font-size: 18px; color: #0f172a; margin: 0 0 4px; }
.header p { font-size: 11px; color: #64748b; margin: 2px 0; }
.footer { margin-top: 32px; border-top: 1px solid #e2e8f0; padding-top: 12px; color: #94a3b8; font-size: 10px; text-align: center; }
table { border-collapse: collapse; width: 100%; }
td, th { border: 1px solid #e2e8f0; padding: 6px 10px; font-size: 13px; }
.copy-btn { position: fixed; top: 10px; right: 10px; background: #9D174D; color: white; border: none; padding: 8px 16px; border-radius: 8px; cursor: pointer; font-size: 12px; z-index: 100; }
</style></head><body>
<button class="copy-btn no-print" onclick="navigator.clipboard.writeText(document.querySelector('.content').innerText).then(()=>this.textContent='Copied!').catch(()=>this.textContent='Copy failed')">Copy All Text</button>
<div class="header">
  <h2>GYNOVATE AI/SI&trade; &mdash; ${message.mode || 'Clinical Output'}</h2>
  <p>Date: ${new Date(message.timestamp).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })} | Time: ${new Date(message.timestamp).toLocaleTimeString('en-IN')}</p>
  <p>Patient Name: __________________ &nbsp;&nbsp; UHID: __________________ &nbsp;&nbsp; Age/Sex: ____/____</p>
</div>
<div class="content">${content}</div>
<div class="footer">
  Generated by GYNOVATE AI/SI&trade; | Created by Dr. Vikram Dukle, Candolim, Goa &ndash; India<br/>
  This is AI-generated clinical decision support. Verify with clinical judgement before use.
</div>
</body></html>`);
    win.document.close();
    win.print();
  };

  const handleExportPptx = async () => {
    setExportingPptx(true);
    try {
      const res = await axios.post(`${API}/export/pptx`, {
        content: message.content,
        title: message.mode || 'GYNOVATE AI/SI™ Presentation',
      }, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'gynovate_presentation.pptx';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PPTX downloaded!');
    } catch {
      toast.error('PPTX export failed');
    } finally {
      setExportingPptx(false);
    }
  };

  const [exportingDocx, setExportingDocx] = useState(false);

  const handleExportDocx = async () => {
    setExportingDocx(true);
    try {
      // Pull letterhead from localStorage so Word doc is pre-branded
      let letterhead = null;
      try {
        const raw = localStorage.getItem('gynovate_letterhead');
        if (raw) letterhead = JSON.parse(raw);
      } catch { /* ignore */ }

      const modeSlug = (message.mode || 'gynovate_document')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 40);
      const filename = `gynovate_${modeSlug}_${new Date().toISOString().slice(0,10)}.docx`;

      const res = await axios.post(
        `${API}/api/export/docx`,
        {
          content: message.content,
          title: message.mode || 'Gynovate AI/SI Document',
          mode: message.mode,
          filename,
          letterhead,
        },
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Word document downloaded');
    } catch (e) {
      toast.error('Word export failed');
    } finally {
      setExportingDocx(false);
    }
  };

  if (isUser) {
    return (
      <div
        data-testid={`message-user-${message.id}`}
        className="flex justify-end px-4 py-2 animate-fade-in"
      >
        <div className="max-w-[70%]">
          {message.mode && message.mode !== 'General' && (
            <div className="flex justify-end mb-1">
              <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${mode?.badge || 'bg-gray-100 text-gray-600'}`}>
                {message.mode}
              </span>
            </div>
          )}
          {/* Show uploaded file/image */}
          {(message.file_url || message.file_preview) && (
            <div className="mb-2 rounded-xl overflow-hidden border border-raspberry-200">
              {message.file_type?.startsWith('image/') ? (
                <img
                  src={message.file_preview || `${API.replace('/api', '')}${message.file_url}`}
                  alt={message.file_name || 'Uploaded image'}
                  className="max-w-full max-h-64 object-contain bg-gray-50"
                />
              ) : (
                <div className="flex items-center gap-2 p-3 bg-raspberry-50">
                  <FileText className="w-5 h-5 text-raspberry-600" />
                  <span className="text-sm text-raspberry-800">{message.file_name}</span>
                </div>
              )}
            </div>
          )}
          <div className="bg-raspberry-800 text-white rounded-2xl rounded-tr-sm px-4 py-3 shadow-sm">
            <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={`message-ai-${message.id}`}
      className="flex gap-3 px-4 py-2 animate-fade-in"
    >
      {/* AI Avatar */}
      <div className="w-8 h-8 rounded-xl bg-raspberry-800 flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm">
        <Zap className="w-4 h-4 text-white" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 max-w-[85%]">
        <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-bold text-gray-900">GYNOVATE AI/SI<sup>™</sup></span>
          {message.mode && message.mode !== 'General' && (
            <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${mode?.badge || 'bg-gray-100 text-gray-600'}`}>
              {message.mode}
            </span>
          )}
        </div>

        <div className="bg-white rounded-2xl rounded-tl-sm px-5 py-4 shadow-sm border border-gray-100 border-l-[3px] border-l-raspberry-200">
          {!message.content && message.isStreaming ? (
            <div data-testid="thinking-indicator">
              <div className="flex items-center gap-1.5 mb-2">
                {[0, 1, 2].map(i => (
                  <span
                    key={i}
                    className="w-2 h-2 rounded-full bg-raspberry-300 animate-pulse"
                    style={{ animationDelay: `${i * 0.15}s` }}
                  />
                ))}
                <span className="text-xs text-slate-400 ml-1">Thinking...</span>
              </div>
              <div className="text-xs text-slate-400 italic border-t border-slate-100 pt-2">
                <span className="text-slate-500 font-medium">OBGYN History:</span>{' '}
                {OBGYN_HISTORY[Math.floor(Math.random() * OBGYN_HISTORY.length)]}
              </div>
            </div>
          ) : (
            <>
              <MarkdownRenderer content={message.content} />
              {message.isStreaming && (
                <span className="inline-block w-0.5 h-4 bg-raspberry-500 ml-0.5 animate-pulse" style={{ verticalAlign: 'text-bottom' }} />
              )}
            </>
          )}
          {/* Media display */}
          {message.media?.type === 'image' && message.media?.url && (
            <div className="mt-4 rounded-xl overflow-hidden border border-gray-100">
              <img
                src={message.media.url}
                alt="Generated medical image"
                className="w-full max-h-96 object-contain bg-gray-50"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <a
                href={message.media.url}
                download="gynovate_image.png"
                className="flex items-center gap-1.5 px-3 py-2 text-xs text-gray-500 hover:text-gray-800 border-t border-gray-100 hover:bg-gray-50 transition-colors"
              >
                <Download className="w-3.5 h-3.5" /> Download Image
              </a>
            </div>
          )}
          {message.media?.type === 'video_pending' && (
            <div className="mt-4 flex items-center gap-3 p-4 bg-cyan-50 rounded-xl border border-cyan-100">
              <Loader2 className="w-5 h-5 text-cyan-600 animate-spin flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-cyan-800">Generating with Sora 2...</p>
                <p className="text-xs text-cyan-600 mt-0.5">This takes 2–5 minutes. The video will appear here automatically.</p>
              </div>
            </div>
          )}
          {message.media?.type === 'video' && message.media?.url && (
            <div className="mt-4 rounded-xl overflow-hidden border border-gray-100">
              <video controls className="w-full max-h-80 bg-black rounded-xl">
                <source src={message.media.url} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          )}
          {message.media?.type === 'video_error' && (
            <div className="mt-4 flex items-center gap-2 p-3 bg-red-50 rounded-xl border border-red-100">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <p className="text-xs text-red-700">Video generation failed. Please try again.</p>
            </div>
          )}
        </div>

        {/* Actions - only show when content exists and not streaming */}
        {message.content && !message.isStreaming && (
        <div className="flex items-center gap-1 mt-2 flex-wrap">
          <button
            data-testid={`copy-btn-${message.id}`}
            onClick={handleCopy}
            className={`flex items-center gap-1.5 text-xs py-1 px-2 rounded-lg transition-colors duration-150 ${
              PRINT_MODES.includes(message.mode)
                ? 'text-raspberry-600 hover:text-raspberry-800 hover:bg-raspberry-50 border border-raspberry-200 font-medium'
                : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? 'Copied' : 'Copy'}
          </button>

          {/* Latest Evidence — live PubMed 2026 */}
          {onLatestEvidence && (
            <button
              data-testid={`latest-evidence-btn-${message.id}`}
              onClick={onLatestEvidence}
              className="flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-800 py-1 px-2 rounded-lg hover:bg-indigo-50 transition-colors duration-150 font-semibold"
              title="Fetch the newest 2025–26 PubMed literature on this topic"
            >
              <Zap className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Latest Evidence</span>
              <span className="sm:hidden">2026</span>
            </button>
          )}

          {/* WhatsApp Share – all AI messages */}
          <div className="relative">
            <button
              data-testid={`whatsapp-btn-${message.id}`}
              onClick={() => setShowWhatsAppForm(!showWhatsAppForm)}
              className="flex items-center gap-1.5 text-xs text-green-600 hover:text-green-800 py-1 px-2 rounded-lg hover:bg-green-50 transition-colors duration-150"
            >
              <MessageSquare className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">WhatsApp</span>
            </button>

            {showWhatsAppForm && (
              <div
                className="absolute bottom-10 left-0 z-30 bg-white border border-slate-200 rounded-xl shadow-xl p-4 w-72"
                data-testid="whatsapp-form"
                onClick={e => e.stopPropagation()}
              >
                <p className="text-xs font-bold text-slate-700 mb-3">Share via WhatsApp</p>
                <input
                  type="text"
                  value={waDocName}
                  onChange={e => setWaDocName(e.target.value)}
                  placeholder="Doctor Name (e.g. Dr. Sharma)"
                  className="w-full px-3 py-2 text-xs rounded-lg border border-slate-200 bg-slate-50 mb-2 focus:outline-none focus:border-green-400"
                  data-testid="wa-doc-name"
                />
                <input
                  type="text"
                  value={waClinicName}
                  onChange={e => setWaClinicName(e.target.value)}
                  placeholder="Clinic Name (e.g. City Hospital)"
                  className="w-full px-3 py-2 text-xs rounded-lg border border-slate-200 bg-slate-50 mb-3 focus:outline-none focus:border-green-400"
                  data-testid="wa-clinic-name"
                />
                <div className="flex flex-col gap-2">
                  {(() => {
                    // Precompute the WhatsApp URL so the <a> tag opens the link
                    // immediately on click (no popup-blocker, no async work in handler).
                    const content = (message.content || '')
                      .replace(/\*\*([^*]+)\*\*/g, '*$1*')
                      .replace(/^#{1,6} ([^\n]+)/gm, '*$1*')
                      .replace(/^- /gm, '  • ')
                      .replace(/^(\d+)\. /gm, '  $1. ')
                      .split('\n')
                      .filter(line => !/^(if you (share|provide|include|send|give)|let me know|please (share|provide|send)|share your|provide your|once you share|if you'd like|feel free to share)/i.test(line.trim()))
                      .join('\n')
                      .trim();
                    const header = waClinicName
                      ? `*${waClinicName.toUpperCase()}*\n${waDocName ? `_${waDocName}_` : ''}\n${'━'.repeat(20)}`
                      : waDocName ? `_${waDocName}_\n${'━'.repeat(20)}` : '';
                    const body = [header, '', `*${message.mode || 'Clinical Information'}*`, '', content].filter(Boolean).join('\n');
                    const waUrl = `https://wa.me/?text=${encodeURIComponent(body)}`;
                    return (
                      <>
                        <a
                          href={waUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="wa-send-btn"
                          onClick={() => {
                            try {
                              localStorage.setItem('gynovate_doc_name', waDocName);
                              localStorage.setItem('gynovate_clinic_name', waClinicName);
                            } catch { /* ignore */ }
                            toast.success('Opening WhatsApp…');
                            setTimeout(() => setShowWhatsAppForm(false), 600);
                          }}
                          className="w-full py-2 text-xs font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 text-center"
                        >
                          Send via WhatsApp
                        </a>
                        <button
                          type="button"
                          data-testid="wa-copy-btn"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(body);
                              toast.success('Copied! Now paste in any WhatsApp chat.');
                              setTimeout(() => setShowWhatsAppForm(false), 600);
                            } catch {
                              toast.error('Could not copy. Long-press the message and copy manually.');
                            }
                          }}
                          className="w-full py-2 text-xs font-semibold text-green-700 bg-green-50 rounded-lg hover:bg-green-100 border border-green-200"
                          title="If WhatsApp link is blocked, use this to copy and paste manually"
                        >
                          Copy to Clipboard (if link is blocked)
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowWhatsAppForm(false)}
                          className="w-full py-1.5 text-[11px] text-slate-500 rounded-lg hover:bg-slate-100"
                        >
                          Cancel
                        </button>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>

          {/* Star / Favourite */}
          <button
            data-testid={`star-btn-${message.id}`}
            onClick={async () => {
              try {
                await axios.post(`${API}/api/favourites`, {
                  message_id: message.id,
                  content: message.content,
                  mode: message.mode,
                  session_id: message.session_id || '',
                  phone: getUserPhone(),
                });
                setStarred(true);
                toast.success('Saved to favourites');
              } catch { toast.error('Failed to save'); }
            }}
            className={`flex items-center gap-1.5 text-xs py-1 px-2 rounded-lg transition-colors duration-150 ${
              starred ? 'text-amber-600 bg-amber-50' : 'text-gray-400 hover:text-amber-600 hover:bg-amber-50'
            }`}
          >
            <Star className={`w-3.5 h-3.5 ${starred ? 'fill-amber-500' : ''}`} />
            <span className="hidden sm:inline">{starred ? 'Saved' : 'Save'}</span>
          </button>

          {/* PDF + PPTX export – Presentation Maker mode */}
          {message.mode === 'Presentation Maker' && (
            <>
              <button
                data-testid={`pdf-btn-${message.id}`}
                onClick={handleExportPDF}
                className="flex items-center gap-1.5 text-xs text-red-600 hover:text-red-800 py-1 px-2 rounded-lg hover:bg-red-50 transition-colors duration-150 border border-red-200"
              >
                <FileText className="w-3.5 h-3.5" />
                Export PDF
              </button>
              <button
                data-testid={`pptx-btn-${message.id}`}
                onClick={handleExportPptx}
                disabled={exportingPptx}
                className="flex items-center gap-1.5 text-xs text-orange-600 hover:text-orange-800 py-1 px-2 rounded-lg hover:bg-orange-50 transition-colors duration-150 border border-orange-200 disabled:opacity-50"
              >
                {exportingPptx ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                {exportingPptx ? 'Exporting...' : 'Export PPTX'}
              </button>
            </>
          )}

          {/* Print-ready export for documentation modes */}
          {PRINT_MODES.includes(message.mode) && message.content && (
            <>
              <button
                data-testid={`print-btn-${message.id}`}
                onClick={handleExportPDF}
                className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 py-1 px-2 rounded-lg hover:bg-blue-50 transition-colors duration-150 border border-blue-200"
              >
                <Printer className="w-3.5 h-3.5" />
                Print / Export PDF
              </button>
              <button
                data-testid={`docx-btn-${message.id}`}
                onClick={handleExportDocx}
                disabled={exportingDocx}
                className="flex items-center gap-1.5 text-xs text-sky-700 hover:text-sky-900 py-1 px-2 rounded-lg hover:bg-sky-50 transition-colors duration-150 border border-sky-200 disabled:opacity-50 font-medium"
                title="Download as Word document with your clinic letterhead baked in"
              >
                {exportingDocx ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
                {exportingDocx ? 'Preparing…' : 'Download Word'}
              </button>
            </>
          )}

          {/* Light-weight DOCX export for non-print-mode AI answers (professional, clinical discussions etc.) */}
          {!PRINT_MODES.includes(message.mode) && message.mode !== 'Presentation Maker' && message.content && (
            <button
              data-testid={`docx-btn-${message.id}`}
              onClick={handleExportDocx}
              disabled={exportingDocx}
              className="flex items-center gap-1.5 text-xs text-sky-600 hover:text-sky-800 py-1 px-2 rounded-lg hover:bg-sky-50 transition-colors duration-150 disabled:opacity-50"
              title="Download as Word document"
            >
              {exportingDocx ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{exportingDocx ? 'Preparing…' : 'Word'}</span>
            </button>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

// Important OBGYN History for loading state - 100+ facts
// Document modes that produce print-ready output
const PRINT_MODES = ['Discharge Summary', 'Consent Format', 'Consent Forms', 'Prescription Builder', 'Prescription Writer',
  'NABH Documentation', 'Referral Letter', 'Referral Letters', 'Medical Certificate', 'Medical Certificates',
  'Ultrasound Report', 'Laparoscopy Report', 'Hysteroscopy Report', 'Robotic Surgery Report',
  'Operative Notes', 'Auto SOAP Notes', 'Voice Case Sheet', 'Lab Investigations',
  'Transfer Summary', 'Clinical Notes', 'Death Summary', 'Medicolegal Help', 'Insurance Claims'];

export const TypingIndicator = () => {
  const [historyFact] = useState(() =>
    OBGYN_HISTORY[Math.floor(Math.random() * OBGYN_HISTORY.length)]
  );

  return (
    <div className="flex gap-3 px-4 py-2 animate-fade-in">
      <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md relative overflow-hidden"
           style={{ background: 'linear-gradient(135deg, #0f172a, #1e293b)' }}>
        <div className="absolute inset-0 animate-shimmer"
             style={{
               background: 'linear-gradient(110deg, transparent 40%, rgba(192,18,60,0.45) 50%, transparent 60%)',
               backgroundSize: '200% 100%',
             }} />
        <Zap className="w-4 h-4 text-white relative z-10" />
      </div>
      <div className="bg-white rounded-2xl rounded-tl-sm px-5 py-4 shadow-sm border border-slate-200 max-w-xl w-full">
        <div className="flex items-center gap-2 mb-3">
          <div className="flex gap-1">
            {[0, 1, 2].map(i => (
              <span
                key={i}
                className="w-1.5 h-1.5 rounded-full"
                style={{
                  background: 'linear-gradient(135deg, #c0123c, #e0a028)',
                  animation: `bounce-dot 1.2s ease-in-out ${i * 0.15}s infinite`,
                }}
              />
            ))}
          </div>
          <span className="text-xs text-slate-500 font-semibold tracking-wide">Composing evidence-based answer…</span>
        </div>

        {/* Progressive skeleton — gives visible structure while awaiting first token */}
        <div className="space-y-2.5 mb-3">
          <div className="relative h-2.5 rounded overflow-hidden bg-slate-100 w-[92%]">
            <div className="absolute inset-0 animate-shimmer"
                 style={{ background: 'linear-gradient(90deg, transparent, rgba(192,18,60,0.15), transparent)', backgroundSize: '200% 100%' }} />
          </div>
          <div className="relative h-2.5 rounded overflow-hidden bg-slate-100 w-[78%]">
            <div className="absolute inset-0 animate-shimmer"
                 style={{ background: 'linear-gradient(90deg, transparent, rgba(192,18,60,0.15), transparent)', backgroundSize: '200% 100%', animationDelay: '0.15s' }} />
          </div>
          <div className="relative h-2.5 rounded overflow-hidden bg-slate-100 w-[85%]">
            <div className="absolute inset-0 animate-shimmer"
                 style={{ background: 'linear-gradient(90deg, transparent, rgba(192,18,60,0.15), transparent)', backgroundSize: '200% 100%', animationDelay: '0.3s' }} />
          </div>
          <div className="relative h-2.5 rounded overflow-hidden bg-slate-100 w-[60%]">
            <div className="absolute inset-0 animate-shimmer"
                 style={{ background: 'linear-gradient(90deg, transparent, rgba(192,18,60,0.15), transparent)', backgroundSize: '200% 100%', animationDelay: '0.45s' }} />
          </div>
        </div>

        <div className="text-[11px] text-slate-400 italic border-t border-slate-100 pt-3 leading-relaxed">
          <span className="text-slate-500 font-semibold not-italic">OBGYN History:</span> {historyFact}
        </div>
      </div>
    </div>
  );
};

export default MessageBubble;
